import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import Select from 'react-select';
import { AgingReportDto, AgingStatusDto, UpdateAgingStateRequest } from '../../../../interfaces/agingReport/agingReport';
import { UpdateAgingStateAsync } from '../../../../services/agingService/agingService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { toast } from 'react-toastify';
import '../AgingReport.css';

interface EditAgingModalProps {
    showEditAgingModal: boolean;
    handlCloseEditAgingModal: () => void;
    selectedAging: AgingReportDto;
    reload : () => void;
    statuses : AgingStatusDto[];
}

const EditAgingModal: React.FC<EditAgingModalProps> = ({
    showEditAgingModal,
    handlCloseEditAgingModal,
    selectedAging,
    reload,
    statuses
}) => {

    const { currentUser } = useAuth();
    const [loading, setLoading] = useState<boolean>(false);

    // Form fields
    const [statusId, setStatusId] = useState<number | null>(null);
    const [comment, setComment] = useState<string>("");



    // Pre-fill form when the modal opens
    useEffect(() => {
        if (selectedAging && statuses.length > 0) {
            const matchedStatus = statuses.find(x => x.name === selectedAging.currentStatus);
            setStatusId(matchedStatus?.id ?? null);
            setComment(selectedAging.latestComment ?? "");
        }
    }, [selectedAging, statuses]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!statusId) {
            toast.error("Please select a status");
            return;
        }

        setLoading(true);

        try {
            if (!currentUser) throw new Error("User not authenticated");
            const token = await currentUser.getIdToken();
            if(!statusId && !comment) return;

            const request: UpdateAgingStateRequest = {
                statusId: statusId,
                comment: comment
            };

            const partID = Number(selectedAging.participantId);
            const res = await UpdateAgingStateAsync(request, partID, token);

            if (res.success) {
                toast.success(res.message);
                reload();
                handlCloseEditAgingModal();
            } else {
                toast.error(res.message || "Failed to update aging state");
            }
        } catch (err: any) {
            toast.error(err.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    // React-select options format
    const statusOptions = statuses.map(s => ({
        value: s.id,
        label: s.name
    }));

    const selectedStatusOption = statusOptions.find(x => x.value === statusId) || null;

    return (
        <Modal
            show={showEditAgingModal}
            onHide={handlCloseEditAgingModal}
            centered
            className="custom_popup aging-comment-modal with_close_icon"
            size="lg"
            backdrop={loading ? "static" : true}
            keyboard={!loading}
        >
            <Modal.Header closeButton={!loading} className="aging-comment-modal-header mb-3">
                <Modal.Title className="aging-comment-modal-title">
                    Comment - {selectedAging?.firstName} {selectedAging?.lastName}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="aging-comment-modal-body">
                <Form onSubmit={handleSubmit}>

                    {/* React Select Status Dropdown */}
                    <Form.Group className="mb-3">
                        <Select
                            options={statusOptions}
                            value={selectedStatusOption}
                            onChange={(option) => setStatusId(option ? option.value : null)}
                            isClearable
                            className="react-select"
                            classNamePrefix="react-select"
                            isDisabled={loading}
                            placeholder="Select status"
                        />
                    </Form.Group>

                    {/* Comment Textarea */}
                    <Form.Group className="mb-0">
                        <Form.Label className="aging-comment-label">Add/Edit Comment</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter your comment here..."
                            disabled={loading}
                            className="aging-comment-textarea"
                        />
                    </Form.Group>

                </Form>
            </Modal.Body>

            <Modal.Footer className="aging-comment-modal-footer">
                <Button 
                    variant="outline-secondary" 
                    onClick={handlCloseEditAgingModal} 
                    disabled={loading}
                    className="aging-comment-cancel-btn"
                >
                    Cancel
                </Button>
                <Button 
                    variant="primary"
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="aging-comment-save-btn"
                >
                    {loading ? <span> <Spinner size='sm' /> Saving...</span> : "Save"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditAgingModal;
